
import React from "react";
import { Card, CardContent } from "@/components/ui/card";
import { Separator } from "@/components/ui/separator";

interface Translation {
  language: string;
  nativeScript: string;
  transliteration: string;
  definition: string;
  note: string;
}

interface KeyUsage {
  reference: string;
  verse: string;
  briefNote: string;
}

interface EtymologyData {
  term: string;
  primaryOriginLanguage: string;
  primaryWordOrPhrase: string;
  translations: Translation[];
  keyUsages: KeyUsage[];
  relatedTerms: string[];
  theologicalInsight: string;
}

interface EtymologyResultProps {
  etymology: EtymologyData;
}

const EtymologyResult: React.FC<EtymologyResultProps> = ({ etymology }) => {
  // Helper to get appropriate color class based on language
  const getLanguageColor = (language: string): string => {
    switch (language.toLowerCase()) {
      case "hebrew":
        return "bg-blue-50 text-blue-700 border-blue-200";
      case "greek":
        return "bg-purple-50 text-purple-700 border-purple-200";
      case "aramaic":
        return "bg-amber-50 text-amber-700 border-amber-200";
      case "latin":
        return "bg-red-50 text-red-700 border-red-200";
      default:
        return "bg-gray-50 text-gray-700 border-gray-200";
    }
  };

  return (
    <div className="bg-white p-6 rounded-xl shadow-md border border-heaven-gold/10 mb-6">
      <h2 className="text-xl md:text-2xl font-playfair font-bold text-selah-navy mb-1">
        Etymology of "{etymology.term}"
      </h2>
      <p className="text-sm md:text-base text-gray-600 mb-4">
        Primary origin: <span className="font-medium">{etymology.primaryOriginLanguage}</span> - 
        {etymology.primaryWordOrPhrase}
      </p>
      
      <div className="mb-6">
        <h3 className="text-base md:text-lg font-semibold text-selah-navy mb-3">Translations</h3>
        <div className="grid gap-4 md:grid-cols-2">
          {etymology.translations.map((translation, index) => (
            <Card key={index} className={`border overflow-hidden ${getLanguageColor(translation.language)}`}>
              <CardContent className="p-3 md:p-4">
                <div className="flex justify-between items-center mb-2">
                  <h4 className="font-bold text-sm md:text-base">{translation.language}</h4>
                </div>
                <p className="text-base md:text-lg mb-1 font-semibold break-words">{translation.nativeScript}</p>
                <p className="text-xs md:text-sm italic mb-2">Transliteration: {translation.transliteration}</p>
                <p className="text-xs md:text-sm font-medium mb-1">Definition:</p>
                <p className="text-xs md:text-sm mb-2 break-words">{translation.definition}</p>
                <p className="text-xs opacity-80 break-words">{translation.note}</p>
              </CardContent>
            </Card>
          ))}
        </div>
      </div>
      
      <div className="mb-6">
        <h3 className="text-base md:text-lg font-semibold text-selah-navy mb-3">Key Scripture Usages</h3>
        <div className="space-y-4">
          {etymology.keyUsages.map((usage, index) => (
            <div key={index} className="bg-heaven-gold/5 p-3 md:p-4 rounded-lg">
              <p className="font-bold text-heaven-gold text-sm md:text-base">{usage.reference}</p>
              <p className="italic my-1 text-gray-700 text-xs md:text-sm">"{usage.verse}"</p>
              <p className="text-xs md:text-sm text-gray-600">{usage.briefNote}</p>
            </div>
          ))}
        </div>
      </div>
      
      <div className="mb-6">
        <h3 className="text-base md:text-lg font-semibold text-selah-navy mb-2">Related Terms</h3>
        <div className="flex flex-wrap gap-2">
          {etymology.relatedTerms.map((term, index) => (
            <span 
              key={index} 
              className="bg-gray-100 text-gray-700 px-2 md:px-3 py-1 rounded-full text-xs md:text-sm"
            >
              {term}
            </span>
          ))}
        </div>
      </div>
      
      <Separator className="my-4" />
      
      <div>
        <h3 className="text-base md:text-lg font-semibold text-selah-navy mb-2">Theological Reflection</h3>
        <p className="text-xs md:text-sm text-gray-700">{etymology.theologicalInsight}</p>
      </div>
    </div>
  );
};

export default EtymologyResult;
